import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Star, MapPin, Image as ImageIcon, Building2, Mail, Boxes, TriangleAlert, ShoppingCart } from "lucide-react";
import { api, euros } from "@/lib/api";

const PERIODS = [
  { id: "today", label: "Aujourd'hui" },
  { id: "7d", label: "7 jours" },
  { id: "30d", label: "30 jours" },
  { id: "month", label: "Ce mois" },
  { id: "custom", label: "Personnalisée" },
];

function computeRange(period, customStart, customEnd) {
  const today = new Date().toISOString().slice(0, 10);
  if (period === "today") return { start: today, end: today };
  if (period === "7d") return { start: new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10), end: today };
  if (period === "30d") return { start: new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10), end: today };
  if (period === "month") return { start: today.slice(0, 8) + "01", end: today };
  return { start: customStart, end: customEnd };
}

export default function Dashboard() {
  const [period, setPeriod] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState(null);

  const { start, end } = useMemo(() => computeRange(period, customStart, customEnd), [period, customStart, customEnd]);

  useEffect(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;
    const params = new URLSearchParams();
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    api.get(`/admin/stats?${params}`).then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/admin/dashboard").then((r) => setContent(r.data)).catch(() => {});
  }, [period, start, end, customStart, customEnd]);

  const bs = stats?.by_status || {};
  const maxRevenue = Math.max(1, ...(stats?.daily || []).map((d) => d.revenue));

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx">Tableau de bord</h1>
        <div className="flex gap-1.5 flex-wrap" data-testid="period-selector">
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`period-${p.id}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${period === p.id ? "bg-onyx text-cream border-onyx" : "bg-white border-line text-onyx hover:border-rouge"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <div className="flex gap-3 mb-6 flex-wrap" data-testid="custom-range">
          <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="custom-start" className="border border-input bg-white px-3 py-2 text-sm" />
          <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="custom-end" className="border border-input bg-white px-3 py-2 text-sm" />
        </div>
      )}

      {!stats ? <p className="text-muted-foreground">Chargement…</p> : (
        <>
          {stats.stock && stats.stock_available <= (stats.stock.threshold ?? 5) && (
            <Link to="/admin/stock" data-testid="stock-alert"
              className="mb-6 flex items-center gap-3 bg-rouge text-white p-4 text-sm font-semibold hover:bg-rouge-hover transition-colors">
              <TriangleAlert className="w-5 h-5 shrink-0" />
              Alerte stock : {stats.stock_available} boîte(s) disponible(s) — seuil défini à {stats.stock.threshold}.
            </Link>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-testid="sales-stats">
            {[
              { label: "Chiffre d'affaires", value: euros(stats.revenue), id: "revenue" },
              { label: "Commandes", value: stats.orders_count, id: "orders" },
              { label: "Jeux vendus", value: stats.units_sold, id: "units" },
              { label: "Panier moyen", value: euros(stats.avg_cart), id: "avg" },
              { label: "Remboursements", value: `${euros(stats.refunds)} (${stats.refunds_count})`, id: "refunds" },
            ].map((c) => (
              <div key={c.id} className="bg-white border border-line p-5" data-testid={`stat-${c.id}`}>
                <p className="font-display text-2xl font-extrabold text-onyx">{c.value}</p>
                <p className="text-xs text-muted-foreground mt-1 font-medium">{c.label}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="status-stats">
            {[
              { label: "En attente paiement", value: bs.en_attente_paiement || 0, id: "pending" },
              { label: "À préparer", value: bs.a_preparer || 0, id: "prepare" },
              { label: "Préparées", value: bs.preparee || 0, id: "prepared" },
              { label: "Expédiées", value: bs.expediee || 0, id: "shipped" },
              { label: "À retirer", value: bs.disponible_retrait || 0, id: "pickup" },
              { label: "Livrées", value: bs.livree || 0, id: "delivered" },
            ].map((c) => (
              <Link key={c.id} to="/admin/commandes" className="bg-white border border-line p-4 hover:border-rouge/40 transition-colors" data-testid={`status-${c.id}`}>
                <p className="font-display text-xl font-extrabold text-onyx">{c.value}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 font-medium leading-tight">{c.label}</p>
              </Link>
            ))}
          </div>

          <section className="bg-white border border-line p-6 mb-10" data-testid="sales-chart">
            <h2 className="font-display font-bold text-lg text-onyx mb-5">Évolution des ventes</h2>
            {stats.daily?.length ? (
              <div className="flex items-end gap-1.5 h-36">
                {stats.daily.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group" title={`${d.date} : ${euros(d.revenue)} (${d.orders} cmd)`}>
                    <div className="w-full bg-rouge/85 group-hover:bg-rouge transition-colors" style={{ height: `${Math.max(3, (d.revenue / maxRevenue) * 100)}%` }} />
                    <p className="text-[9px] text-muted-foreground mt-1 font-mono hidden sm:block">{d.date.slice(5)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-36 flex flex-col items-center justify-center text-center border border-dashed border-line">
                <ShoppingCart className="w-6 h-6 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Aucune vente sur la période.</p>
                <p className="text-xs text-muted-foreground mt-1">Les données Stripe s'afficheront ici dès l'activation du paiement (phase 2).</p>
              </div>
            )}
          </section>
        </>
      )}

      {content && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="content-stats">
          {[
            { label: "Articles", value: content.posts, icon: Newspaper, to: "/admin/blog", id: "posts" },
            { label: "Avis", value: content.reviews, icon: Star, to: "/admin/avis", id: "reviews" },
            { label: "Points de vente", value: content.points_of_sale, icon: MapPin, to: "/admin/points-vente", id: "pos" },
            { label: "Médias", value: content.media, icon: ImageIcon, to: "/admin/medias", id: "media" },
            { label: "Demandes B2B", value: content.inquiries_new, icon: Building2, to: "/admin/entreprises", id: "inquiries", highlight: content.inquiries_new > 0 },
            { label: "Messages non lus", value: content.messages_unread, icon: Mail, to: "/admin/entreprises", id: "messages", highlight: content.messages_unread > 0 },
          ].map(({ label, value, icon: Icon, to, id, highlight }) => (
            <Link key={id} to={to} data-testid={`stat-content-${id}`}
              className={`bg-white border p-4 hover:-translate-y-0.5 transition-transform ${highlight ? "border-rouge" : "border-line"}`}>
              <Icon className={`w-4 h-4 mb-2 ${highlight ? "text-rouge" : "text-muted-foreground"}`} />
              <p className="font-display text-2xl font-extrabold text-onyx">{value ?? 0}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">{label}</p>
            </Link>
          ))}
        </div>
      )}

      {stats?.stock && (
        <Link to="/admin/stock" className="mt-6 flex items-center gap-3 bg-white border border-line p-4 text-sm hover:border-rouge/40 transition-colors" data-testid="stock-summary">
          <Boxes className="w-5 h-5 text-rouge shrink-0" />
          <span>Stock : <strong>{stats.stock_available}</strong> disponible(s) · {stats.stock.reserved || 0} réservée(s) · {stats.stock.sold || 0} vendue(s)</span>
        </Link>
      )}
    </div>
  );
}
